import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../context/AuthContext";
import useAxios from "../../../hooks/useAxios";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import AlertModal from "../../../components/common/alertModal/AlertModal";
import CachedIcon from '@mui/icons-material/Cached';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Header from "../../../components/Header/Header";
import { useNavigate } from "react-router-dom";
import "./PaymentPage.css";

const navLinks = [];

const PaymentPage = () => {
  const { role, status } = useContext(AuthContext);
  const axiosInstance = useAxios();
  const navigate = useNavigate();
  const [monthlyCost, setMonthlyCost] = useState(null);
  const [yearlyCost, setYearlyCost] = useState(null);
  const [cashFlag, setCashFlag] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [error, setError] = useState("");
  const [schoolRequest, setSchoolRequest] = useState(null);

  const [alert, setAlert] = useState({
    open: false,
    title: "",
    message: "",
    type: "success",
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/subscriptions/costs");
      if (res.data.success) {
        setMonthlyCost(res.data.monthlyCost);
        setYearlyCost(res.data.yearlyCost);
        setCashFlag(res.data.cash_flag);
        console.log(res.data);
      } else {
        setError("Failed to fetch costs.");
      }
    } catch (e) {
      setError("Error fetching costs.");
    } finally {
      setLoading(false);
    }
  };

  const fetchRequestStatus = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/school-requests/status");
      if (res.data.success) {
        setSchoolRequest(res.data.request);
      } else {
        setError("Failed to fetch costs.");
      }
    } catch (e) {
      setError("Error fetching costs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchRequestStatus();
  }, []);

  useEffect(() => {
    if (cashFlag) {
      navigate('/dashboard');
    }
  }, [cashFlag]);

  const handleRefresh = () => {
    fetchData();
    fetchRequestStatus();
  }

  if (status === "active") {
    return (
      <Box className="payment-page">
        <Typography variant="h4" className="active-status-text">
          Your subscription is already active. No payment is needed.
        </Typography>
      </Box>
    );
  }

  if (schoolRequest?.status === "rejected") {
    return (
      <>
        <Header navLinks={navLinks} isScrollEffect={false} />
          <Box className="payment-page page-container" textAlign="center">
            <Typography variant="h4" className="rejected-status-text mb-2">
              Sorry, your request was rejected by admins.
            </Typography>
            <Typography className="pending-info-text">
              You can contact us for more information.
            </Typography>
            <Button
              variant="contained"
              className="secondary-button mt-5"
              onClick={handleRefresh}
            >
              <CachedIcon className="mx-2" />
              Refresh
            </Button>
          </Box>
      </>
    );
  }

  const isSchoolPendingPricing =
    role === "school" && Number(monthlyCost) === 0 && Number(yearlyCost) === 0;

  const handleChoosePlan = (plan) => {
    setSelectedPlan(plan);
    setShowConfirm(true);
  };

  const handleConfirmSubscription = async () => {
    setConfirmLoading(true);
    setError("");
    try {
      const res = await axiosInstance.post("/subscriptions/plan-checkout", {
        billing_cycle: selectedPlan,
      });
      if (res.data.success && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setAlert({
          open: true,
          title: "Error",
          message: res.data.message || "Failed to create checkout session.",
          type: "error",
        });
      }
    } catch (err) {
      setAlert({
        open: true,
        title: "Error",
        message: err.response?.data?.message || "Error creating checkout session.",
        type: "error",
      });
    } finally {
      setConfirmLoading(false);
      setShowConfirm(false);
    }
  };

  const getEstimatedEndDate = () => {
    const now = new Date();
    if (selectedPlan === "yearly") {
      now.setFullYear(now.getFullYear() + 1);
    } else {
      now.setMonth(now.getMonth() + 1);
    }
    return now.toDateString();
  };

  const getPlanDetails = (planType) => {
    if (planType === "monthly") {
      return [
        "Manage teacher accounts in your school",
        "Track teachers' classes, assessments, and student progress",
        "Personalized reporting for students",
        "Dedicated reporting based on teachers, students, and assessments",
      ];
    } else {
      return [
        "25% discount",
        "Manage teacher accounts in your school",
        "Track teachers' classes, assessments, and student progress",
        "Personalized reporting for students",
        "Dedicated reporting based on teachers, students, and assessments",
      ];
    }
  };

  if (isSchoolPendingPricing) {
    return (
      <>
        <Header navLinks={navLinks} isScrollEffect={false} />
        <Box className="payment-page page-container" textAlign="center">
          <Typography variant="h4" className="pending-status-text mb-2">
            Your request is pending admin approval.
          </Typography>
          <Typography className="pending-info-text">
            Once the admin assigns a price, you can return here to complete your payment.
          </Typography>
          <Button
            variant="contained"
            className="secondary-button mt-5"
            onClick={() => fetchData()}
          >
            <CachedIcon className="mx-2" />
            Refresh
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <Box className="payment-page page-container">
        <Typography variant="h4" className="page-title">
          Choose Your Subscription Plan
        </Typography>

        {error && <Typography className="error-text">{error}</Typography>}

        {loading ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={8} justifyContent="center" alignItems="stretch" mt={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Card className="plan-card">
                <CardContent>
                  <Avatar className="plan-avatar">M</Avatar>
                  <Typography variant="h5" className="plan-title">
                    Monthly Plan
                  </Typography>
                  <Divider className="divider" />
                  <Typography variant="h3" className="plan-cost">
                    {monthlyCost !== null ? `AED ${monthlyCost}` : "--"}
                  </Typography>
                  <List>
                    {getPlanDetails("monthly").map((detail, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircleIcon  className="check-icon"/>
                        </ListItemIcon>
                        <ListItemText primary={detail} className="plan-description-detail"/>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    className="choose-button secondary-button"
                    onClick={() => handleChoosePlan("monthly")}
                  >
                    Choose Monthly
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card className="plan-card">
                <CardContent>
                  <Avatar className="plan-avatar">Y</Avatar>
                  <Typography variant="h5" className="plan-title">
                    Yearly Plan
                  </Typography>
                  <Divider className="divider" />
                  <Typography variant="h3" className="plan-cost">
                    {yearlyCost !== null ? `AED ${yearlyCost}` : "--"}
                  </Typography>
                  <List>
                    {getPlanDetails("yearly").map((detail, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircleIcon className="check-icon"/>
                        </ListItemIcon>
                        <ListItemText primary={detail} className="plan-description-detail"/>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    className="choose-button secondary-button"
                    onClick={() => handleChoosePlan("yearly")}
                  >
                    Choose Yearly
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        )}

        <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
          <DialogTitle className="dialog-title">Confirm Your Plan</DialogTitle>
          <DialogContent className="dialog-content">
            <Typography variant="h6" className="dialog-plan-name">
              Selected Plan: <strong>{selectedPlan?.charAt(0).toUpperCase() + selectedPlan?.slice(1)} Plan</strong>
            </Typography>
            <Typography variant="body1" className="dialog-cost">
              Cost: <strong>AED {selectedPlan === "monthly" ? monthlyCost : yearlyCost}</strong>
            </Typography>
            <Typography variant="body2" className="dialog-auto-renew">
              This plan will renew automatically. You can cancel anytime from your dashboard.
            </Typography>
            <Typography variant="body2" className="dialog-note">
              By clicking "Proceed to Payment," you will be redirected to perform the online payment process.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirm(false)} className="cancel-button">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmSubscription}
              className="confirm-button"
              disabled={confirmLoading}
            >
              {confirmLoading ? <CircularProgress size={24} /> : "Proceed to Payment"}
            </Button>
          </DialogActions>
        </Dialog>

        <AlertModal
          open={alert.open}
          onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
          title={alert.title}
          message={alert.message}
          type={alert.type}
        />
      </Box>
    </>
  );
};

export default PaymentPage;
